import { useCallback } from 'react';
import { trim } from 'lodash';

// @ts-expect-error - useSelectNewProjectTypePopup doesn't have TypeScript definitions
import { useSelectNewProjectTypePopup } from 'src/components/SelectNewProjectTypePopup';
// @ts-expect-error - useTemplateFoundPopup doesn't have TypeScript definitions
import { useTemplateFoundPopup } from 'src/components/TemplateFoundPopup';
import {
  GENERATION_TOOL,
  type GenerationTool,
  isBlankLongFormGenerationTool,
  isFreestyleGenerationTool,
} from 'src/data/generationTool';
import {
  CLASSIFIED_TEMPLATE_BLOG,
  type ClassifiedTemplateBlog,
  type EditorType,
  isLongFormEditorType,
  isLongFormProjectType,
  isShortFormEditorType,
  PROJECT_TYPE,
} from 'src/data/project';
import { type TemplateClassifyResult } from 'src/data/template';
import * as ls from 'src/lib/localData';
import { type VoidFunction } from 'src/lib/types';
// @ts-expect-error - useCreateProject doesn't have TypeScript definitions
import { useCreateProject } from 'src/services/useCreateProject';
// @ts-expect-error - useErrorHandler doesn't have TypeScript definitions
import { useErrorHandler } from 'src/services/useErrorHandler';
// @ts-expect-error - useProjectTemplatePopupSubmitHandler doesn't have TypeScript definitions
import { useProjectTemplatePopupSubmitHandler } from 'src/services/useProjectTemplatePopupSubmitHandler';
import { useUnsupportedPromptPopup } from 'src/services/useUnsupportedPromptPopup';
// @ts-expect-error - src/store doesn't have TypeScript definitions
import { useMultiPromptTemplateActions, useTemplatesActions } from 'src/store';
// @ts-expect-error - useNotificationActions doesn't have TypeScript definitions
import { useNotificationActions } from 'src/store/notification';
import { useImproveContentTypePopup } from 'src/views/ImproveContentTypePopup';
import {
  PROJECT_TEMPLATE_POPUP_SIDEBAR_TAB,
  useProjectTemplatePopup,
  // @ts-expect-error - ProjectTemplatePopup doesn't have TypeScript definitions
} from 'src/views/ProjectTemplatePopup';

import { useEvents } from './services/useEvents';
import {
  QUICK_NAVIGATION_EVENT,
  WIDGET_ACTION,
  type WidgetAction,
  type WidgetActionData,
} from './lib';
import { useMainActions } from './store';

interface Params {
  onFlowFinished?: VoidFunction;
  inPopupMode?: boolean;
  editorType: EditorType;
}

export const useHandlers = ({ onFlowFinished, inPopupMode, editorType }: Params) => {
  const createProject = useCreateProject();
  const openSelectNewProjectTypePopup = useSelectNewProjectTypePopup();
  const requestClassifiedToolUserApproval = useRequestClassifiedToolUserApproval();
  const openProjectTemplatePopup = useProjectTemplatePopup();
  const openImproveContentTypePopup = useImproveContentTypePopup();
  const handleProjectTemplatePopupSubmit = useProjectTemplatePopupSubmitHandler();
  const handleError = useErrorHandler();
  const events = useEvents({ inPopupMode });

  const openUnsupportedPromptPopup = useUnsupportedPromptPopup();

  const notifications = useNotificationActions();
  const mainActions = useMainActions();
  const templatesActions = useTemplatesActions();
  const multiPromptTemplateActions = useMultiPromptTemplateActions();

  const handleWidgetAction = useCallback(
    async (action: WidgetAction, data?: WidgetActionData) => {
      switch (action) {
        /**
         * Ask user if to open a new "short form" or "long form" content and redirect to general prompt / blank template
         */
        case WIDGET_ACTION.createBlankProject: {
          events.trackQuickNavigation(QUICK_NAVIGATION_EVENT.newBlankProject);

          const params = inPopupMode
            ? {
                title: 'New blank project',
                description: 'Select the editor to use with this project:',
                displayBackButton: true,
              }
            : { narrow: true };

          const result = await openSelectNewProjectTypePopup(params);
          if (!result) {
            return;
          }

          events.trackBlankProject(result.projectType);
          createProject(result.projectType, {
            generationTool: isLongFormProjectType(result.projectType)
              ? GENERATION_TOOL.longFormBlank
              : GENERATION_TOOL.freestyle,
          });
          onFlowFinished?.();
          break;
        }

        /**
         * Process "recently used template card" click - create a new project based on the card's template and sub-template (if applicable)
         */
        case WIDGET_ACTION.createRecentProject: {
          const cardData = data?.recentlyUsedTemplateCardData;
          if (!cardData) {
            return;
          }

          const params: Record<string, unknown> = {
            generationTool: cardData.generationTool,
          };
          if (cardData.customToolData) {
            params.customMultiPromptId = cardData.customToolData.id;
            params.customMultiPromptGroup = cardData.customToolData.templateGroup;
          }
          if (cardData.subTemplateData) {
            params.subTemplateId = cardData.subTemplateData.id;
          }

          createProject(cardData.projectType, params);
          onFlowFinished?.();
          break;
        }

        /**
         * Open content improver menu to select a content type
         */
        case WIDGET_ACTION.improveContent: {
          events.trackQuickNavigation(QUICK_NAVIGATION_EVENT.improveExistingContent);

          const result = await openImproveContentTypePopup();
          if (!result) {
            return;
          }

          if (result.projectType === PROJECT_TYPE.longFormBuilder) {
            createProject(PROJECT_TYPE.longFormBuilder, {
              generationTool: GENERATION_TOOL.longFormContentImprover,
              improveContentType: result.contentType,
            });
          } else {
            createProject(PROJECT_TYPE.document, {
              improveContentType: result.contentType,
            });
          }
          onFlowFinished?.();
          break;
        }

        /**
         * Open the template gallery on default tab (”Most popular”)
         */
        case WIDGET_ACTION.openTemplateGallery: {
          events.trackQuickNavigation(QUICK_NAVIGATION_EVENT.templateGallery);

          openProjectTemplatePopup({
            selectedTabType: PROJECT_TEMPLATE_POPUP_SIDEBAR_TAB.mostPopular,
            onSubmit: (...args: Record<string, unknown>[]) => {
              handleProjectTemplatePopupSubmit(...args);
              onFlowFinished?.();
            },
          });
          break;
        }

        /**
         * Open the template gallery on “Your saved templates” tab
         */
        case WIDGET_ACTION.openSavedTemplates: {
          events.trackQuickNavigation(QUICK_NAVIGATION_EVENT.savedTemplates);

          openProjectTemplatePopup({
            selectedTabType: PROJECT_TEMPLATE_POPUP_SIDEBAR_TAB.savedTemplates,
            onSubmit: (...args: Record<string, unknown>[]) => {
              handleProjectTemplatePopupSubmit(...args);
              onFlowFinished?.();
            },
          });
          break;
        }

        /**
         * Handle "new project prompt" submission
         */
        case WIDGET_ACTION.submitPrompt: {
          events.trackSubmit(mainActions.getPromptText());

          const promptData = mainActions.getPromptData();
          if (!promptData || trim(promptData?.text) === '') {
            notifications.displayWarning('Cannot classify empty prompt');
            return;
          }
          const templates = isShortFormEditorType(editorType)
            ? templatesActions.getSubTemplates()
            : multiPromptTemplateActions.getMultiPromptReleasedTemplates();
          try {
            const classifiedTemplate = await mainActions.classifyTemplate(templates);

            if (
              (isFreestyleGenerationTool(classifiedTemplate.generationTool as GenerationTool) &&
                isLongFormEditorType(editorType)) ||
              isBlankLongFormGenerationTool(classifiedTemplate.generationTool as GenerationTool)
            ) {
              // Classified as freestyle/blank for long form - mark as pre-classified
              // so we don't show the template popup again if prompt unchanged
              const promptData = mainActions.getPromptData();
              createProject(PROJECT_TYPE.longFormBuilder, {
                generationTool: GENERATION_TOOL.longFormBlank,
                promptData,
                preClassifiedPromptText: promptData?.text,
              });
              onFlowFinished?.();
              return;
            }

            if (classifiedTemplate.isInvalidPrompt) {
              openUnsupportedPromptPopup();
              return;
            }

            const approvalResult = await requestClassifiedToolUserApproval({
              classifiedTemplate,
              inPopupMode,
            });
            // If user closed/backed the popup - return to the "widget"
            if (!approvalResult) {
              return;
            }

            // Based on the user's choice, create a new project of specific type
            // If user chose "Stay with prompt" (freestyle or longFormBlank), mark the prompt as pre-classified
            // so we don't show the template popup again if they don't change the prompt
            const promptData = mainActions.getPromptData();
            const isStayWithPrompt =
              approvalResult === GENERATION_TOOL.freestyle ||
              approvalResult === GENERATION_TOOL.longFormBlank;
            const preClassifiedPromptText = isStayWithPrompt ? promptData?.text : null;

            if (approvalResult === GENERATION_TOOL.blogDescription) {
              createProject(PROJECT_TYPE.blogPostBuilder, {
                promptData,
              });
              onFlowFinished?.();
              return;
            } else if (approvalResult === GENERATION_TOOL.customMultiPrompt) {
              createProject(PROJECT_TYPE.longFormBuilder, {
                generationTool: GENERATION_TOOL.longFormBlank,
                promptData,
                customMultiPromptId: classifiedTemplate.id,
              });
              onFlowFinished?.();
              return;
            } else if (
              (approvalResult === GENERATION_TOOL.freestyle && isLongFormEditorType(editorType)) ||
              approvalResult === GENERATION_TOOL.longFormBlank
            ) {
              createProject(PROJECT_TYPE.longFormBuilder, {
                generationTool: GENERATION_TOOL.longFormBlank,
                promptData,
                preClassifiedPromptText,
              });
              onFlowFinished?.();
              return;
            } else {
              // For short form, if user chose freestyle but we classified a template,
              // pass the override so we generate with the classified tool while staying on freestyle UI
              const generationToolOverride =
                approvalResult === GENERATION_TOOL.freestyle && isShortFormEditorType(editorType)
                  ? classifiedTemplate.generationTool
                  : null;

              createProject(PROJECT_TYPE.document, {
                generationTool: approvalResult,
                promptData,
                subTemplateId: classifiedTemplate.id,
                preClassifiedPromptText,
                generationToolOverride,
              });
            }
            onFlowFinished?.();
          } catch (error) {
            handleError(error);
          }
          break;
        }
      }
    },
    [
      events,
      inPopupMode,
      openSelectNewProjectTypePopup,
      createProject,
      onFlowFinished,
      openImproveContentTypePopup,
      openProjectTemplatePopup,
      handleProjectTemplatePopupSubmit,
      mainActions,
      editorType,
      templatesActions,
      multiPromptTemplateActions,
      notifications,
      requestClassifiedToolUserApproval,
      openUnsupportedPromptPopup,
      handleError,
    ]
  );

  return {
    handleWidgetAction,
  };
};

const useRequestClassifiedToolUserApproval = () => {
  const openTemplateFoundPopup = useTemplateFoundPopup();

  return useCallback(
    async ({
      classifiedTemplate,
      inPopupMode,
    }: {
      classifiedTemplate: TemplateClassifyResult;
      inPopupMode?: boolean;
    }) => {
      const alignedTool = classifiedTemplate.generationTool
        ? alignTool(classifiedTemplate.generationTool)
        : undefined;

      // If user has already chosen to not show the popup, return the "classified" tool
      if (ls.templateFoundPopupDoNotShowAgain.read()) {
        return alignedTool;
      }

      // If the tool is "freestyle", no need to ask for user's approval
      if (alignedTool && isFreestyleGenerationTool(alignedTool)) {
        return alignedTool;
      }

      // Request user's approval for the classified tool
      const result = await openTemplateFoundPopup({
        generationTool: alignedTool,
        displayBackButton: inPopupMode,
        templateName: classifiedTemplate.name,
      });
      if (!result) {
        return null;
      }

      // Save user's choice
      if (result.dontShowAgain) {
        ls.templateFoundPopupDoNotShowAgain.write(true);
      }

      const defaultTool =
        alignedTool === GENERATION_TOOL.blogDescription
          ? GENERATION_TOOL.longFormBlank
          : GENERATION_TOOL.freestyle;
      return result.generationTool || defaultTool;
    },
    [openTemplateFoundPopup]
  );
};

const alignTool = (tool: GenerationTool | ClassifiedTemplateBlog): GenerationTool => {
  // BE can return "blog" as a classified tool - we will handle it as "blogDescription" in FE logic
  if (tool === CLASSIFIED_TEMPLATE_BLOG) {
    return GENERATION_TOOL.blogDescription;
  }
  return tool;
};
